/**
 * Looks up default field mappings from the backend incoming-schema
 * for the current app combination (extensionName) and target CRM module.
 *
 * Returns the exact fields and values from UA_APP_INCOMING_DEFAULT_SCHEMA.
 */

import { useAppStore } from '@/stores/app-store';
import { INCOMING_DEFAULT_SCHEMA } from '@/config/incoming-default-schema';

/**
 * Get the default field mappings for a CRM module from the incoming default schema.
 * Returns `{ crmField: tpaValueExpression }` (e.g. `{ Mobile: '${{{$.from}}}' }`).
 *
 * `fields_type` is returned empty — the actual CRM field data types are populated
 * later by the field mapping panel once CRM field definitions are loaded.
 */
export function generateDefaultFieldMappings(
  targetModule: string
): { fields: Record<string, string>; fields_type: Record<string, string> } {
  const emptyResult = { fields: {}, fields_type: {} };

  const extensionName = useAppStore.getState().extensionName;
  if (!extensionName) return emptyResult;

  const appSchema = INCOMING_DEFAULT_SCHEMA[extensionName];
  if (!appSchema) return emptyResult;

  const moduleFields = appSchema[targetModule];
  if (!moduleFields) return emptyResult;

  const fields: Record<string, string> = { ...moduleFields };

  return { fields, fields_type: {} };
}

/**
 * Domain the incoming default schema uses for synthesised email addresses.
 * Presence of this suffix in a mapped value is what marks it as a dummy.
 */
export const DUMMY_EMAIL_DOMAIN_SUFFIX = '.apps.ulgebra.com';

/**
 * The dummy email the incoming default schema fills for this app + module, if any.
 *
 * Some SaaS (FreshSales, Zoho Desk, Zendesk) reject a new record without an email,
 * so the default schema synthesises one from the customer's number
 * (`<phone>@<appCode>.apps.ulgebra.com`). A user-configured field mapping replaces
 * the default fields wholesale on the server, so the mapping editor surfaces this
 * value as a prefill — editable, but present by default.
 *
 * Returns `{ field, value }` for the CRM field that carries it, or null when the
 * app/module has no dummy email (event apps map the invitee's real address).
 */
export function getDefaultDummyEmail(
  targetModule: string
): { field: string; value: string } | null {
  const extensionName = useAppStore.getState().extensionName;
  if (!extensionName) return null;

  const moduleFields = INCOMING_DEFAULT_SCHEMA[extensionName]?.[targetModule];
  if (!moduleFields) return null;

  const entry = Object.entries(moduleFields).find(([, value]) =>
    value.includes(DUMMY_EMAIL_DOMAIN_SUFFIX)
  );
  return entry ? { field: entry[0], value: entry[1] } : null;
}
